import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { BroadcastPresetViewModel } from "../view-models/broadcast-preset.view-model";
import { UI } from "../../../services/ui.service";
import {
  ActivatedRoute,
  Navigation,
  NavigationEnd,
  Params,
  Router,
} from "@angular/router";
import { IBroadcastsRepository } from "../../../domain/broadcasts/i-broadcasts-repository";
import { ICommonUtilInterface } from "../../../domain/common/common-util.interface";
import { Socket } from "../../../infrastructure/socket.service";
import { UserService } from "../../../domain/user-service";
import {
  EChannelBroadcastMode,
  IChannelBroadcastModeChecker,
} from "../../../domain/broadcasts/i-channel-broadcast-mode-checker";
import { ChannelRepository } from "../../../domain/channel-repository";
import { ShowRepository } from "../../../domain/show-repository";
import { CalendarService } from "../../../domain/calendar-service";
import { ILayoutsRepository } from "../../../domain/layouts/layouts.repository";
import { IBlockRepository } from "app/js/src/domain/blocks/i-block.repository";
import { Observable, Subscription } from "rxjs";
import { SwitcherItemViewModel } from "../view-models/switcher-item.view-model";
import { ESwitcherAssetType } from "app/js/src/domain/broadcasts/switcher-item.type.enum";
import { TimeSpotRuleExtentedSettings } from "app/js/src/domain/timespot";
import { IEvrideoDialogService } from "../../../domain/common/i-evrideo-dialog-service";
import { LiveOtherUserEnteredComponent } from "../../live/live-other-user-entered/live-other-user-entered.component";
import { TakeOverConfirmDialogComponent } from "../take-over-confirm-dialog/take-over-confirm-dialog.component";

interface customBroadcastPresetNavigationState {
  navigationId: number;
  fromLiveLink?: boolean;
  fromScheduler?: boolean;
}
@Component({
  selector: "evrideo-broadcast-editor",
  templateUrl: "./broadcast-editor.component.html",
  styleUrls: ["./broadcast-editor.component.scss"],
})
export class BroadcastEditorComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  vm: BroadcastPresetViewModel;
  @ViewChild("previewVideoElement")
  previewVideoElement: ElementRef<HTMLVideoElement>;
  @ViewChild("programVideoElement")
  programVideoElement: ElementRef<HTMLVideoElement>;
  private _routerEndSubscription: Subscription;
  private _routeParamsSubscription: Subscription;
  private _broadcastPresetUpdatedSubscription: Subscription;
  private _broadcastPageClientId: string;
  private _viewInitialized = false;
  private _pendingRouteParams: Params | null = null;
  private _mamCollapsedTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private _otherUserEnteredCleanupTimeoutId: ReturnType<typeof setTimeout> | null =
    null;
  private _debugDragMouseMoveHandler: ((e: MouseEvent) => void) | null = null;
  private _debugDragMouseUpHandler: (() => void) | null = null;
  private _onOtherUserEnteredBroadcastEditBound: (
    broadcastPresetUid: string,
    username: string,
    time: Date,
  ) => void;
  private _onOtherUserEnteredBroadcastLiveBound: (
    broadcastPresetUid: string,
    username: string,
    time: Date,
  ) => void;
  private _isBroadcastEditPage = false;
  private _isBroadcastLivePage = false;
  private _destroyed = false;
  AllowDebugMode = false;
  private _leftShiftPressed = false;
  private _rightShiftPressed = false;
  mamCollapsedSubscription: any;
  public mamCollapsed = false;
  public showStatus = false;
  public resizeModeEnabled = false;
  constructor(
    private _ui: UI,
    private _route: ActivatedRoute,
    private _router: Router,
    private _broadcastsPresetsRepository: IBroadcastsRepository,
    private _commonUtils: ICommonUtilInterface,
    private _socketService: Socket,
    private _changeDetectorRef: ChangeDetectorRef,
    private _userService: UserService,
    private _channelBroadcastModeChecker: IChannelBroadcastModeChecker,
    private _channelRepository: ChannelRepository,
    private _showRepository: ShowRepository,
    private _calendarService: CalendarService,
    private _layoutRepository: ILayoutsRepository,
    private _blockRepository: IBlockRepository,
    private _dialogService: IEvrideoDialogService,
    private _viewContainerRef: ViewContainerRef,
    private _ngZone: NgZone,
  ) {
    this.AllowDebugMode = _userService.getCurrentUser().isSuperUser || false;
    this._routeParamsSubscription = this._route.params.subscribe((params: Params) => {
      if (!params["id"]) {
        return;
      }
      this._pendingRouteParams = params;
      if (this._viewInitialized) {
        this._applyRouteParams();
      }
    });
  }

  ngAfterViewInit(): void {
    this._viewInitialized = true;
    this._changeDetectorRef.detectChanges();
    if (this._pendingRouteParams) {
      this._applyRouteParams();
    }
  }

  private _applyRouteParams(): void {
    const params = this._pendingRouteParams;
    this._pendingRouteParams = null;
    if (!params?.["id"]) {
      return;
    }
    this._runRouteParamsLogic(params).catch((err) => {
      console.error("BroadcastEditorComponent->_applyRouteParams failed", err);
      this._safeDetectChanges();
    });
  }

  /** detectChanges() throws once the view is destroyed (navigated away mid-init). */
  private _safeDetectChanges(): void {
    if (this._destroyed) {
      return;
    }
    this._changeDetectorRef.detectChanges();
  }

  private async _runRouteParamsLogic(params: Params): Promise<void> {
    const broadcastId = params["id"];
    const currentPath = this._router.url;
    const refreshPromise = this._broadcastsPresetsRepository
      .refreshBroadcastPreset(broadcastId)
      .then(() => true)
      .catch((error) => {
        console.warn(
          "BroadcastEditorComponent->_runRouteParamsLogic refresh failed",
          error,
        );
        return false;
      });
    const [refreshSucceeded, canProceed] = await Promise.all([
      refreshPromise,
      this._handlePagePresenceAndTakeover(broadcastId, currentPath),
    ]);
    if (!canProceed) {
      return;
    }

    const existingBroadcast = (
      await this._broadcastsPresetsRepository.get(null, broadcastId)
    )[0];
    if (!existingBroadcast) {
      this._router.navigate(["/scheduler/calendar"]).then();
      return;
    }
    if (!refreshSucceeded) {
      console.warn(
        "Live Sync Warning: Unable to refresh broadcast from server. Showing cached data.",
      );
    }
    this.vm?.onDestroy();

    this.vm = new BroadcastPresetViewModel(
      existingBroadcast,
      this._ui,
      this._broadcastsPresetsRepository,
      this._commonUtils,
      this._router,
      this._socketService,
      this._changeDetectorRef,
      this._userService,
      this._channelBroadcastModeChecker,
      this._channelRepository,
      this._showRepository,
      this._calendarService,
      this._layoutRepository,
      this._blockRepository,
    );
    // The template is gated on *ngIf="vm", and nothing below is guaranteed to
    // complete (SignalR round-trips can reject or hang). Render the shell as
    // soon as the view model exists so a failure downstream can't leave the
    // page blank until the user clicks and incidentally triggers a tick.
    this._safeDetectChanges();

    try {
      // Must run before onInit(): the mode-change subscription created inside
      // onInit() can fire while onInit() is still awaiting, and it uses
      // allowLiveMode to decide whether the program window binds to the
      // operational channel or to the preset's own program channel.
      if (!(await this.checkIfToAllowLiveMode())) {
        return;
      }
      await this.vm.onInit();
      this.showStatus = true;
    } finally {
      this._safeDetectChanges();
    }
  }

  private async _handlePagePresenceAndTakeover(
    broadcastId: string,
    currentPath: string,
  ): Promise<boolean> {
    if (currentPath.startsWith("/broadcasts/edit/")) {
      this._broadcastPageClientId =
        this._commonUtils.generateGuid() + "_broadcastPage";
      const currentUser = this._userService.getCurrentUser();
      const channel = await this._userService.getCurrentChannel();
      const channelState = channel
        ? this._channelBroadcastModeChecker.getStateForChannel(channel.id)
        : null;
      const presetInPreLiveOrLive =
        channelState &&
        channelState.mode !== EChannelBroadcastMode.Off &&
        (channelState.nextLiveBroadcastPresetShow?.broadcastPresetUid ===
          broadcastId ||
          channelState.liveBroadcastPresetShowUid === broadcastId);
      if (presetInPreLiveOrLive) {
        await this._dialogService.openDialog(this._viewContainerRef, {
          type: "warning",
          title: "Cannot edit preset",
          hideCloseButton: true,
          hideIcon: true,
          backDropOpacity: 0.9,
          confirmButtonText: "CLOSE",
          content:
            "This preset is in pre-live or live. Editing is not allowed.",
          timeout: null as any,
        });
        this._router.navigateByUrl("/scheduler/calendar");
        return false;
      }
      this._onOtherUserEnteredBroadcastEditBound =
        this._onOtherUserEnteredBroadcastEdit.bind(this);
      const enterResult = await this._socketService.enteredBroadcastEditPageEventHandler(
        broadcastId,
        currentUser.username,
        this._broadcastPageClientId,
        this._onOtherUserEnteredBroadcastEditBound,
      );
      if (enterResult.alreadyInUse) {
        const confirmed = await this._dialogService.openDialog(
          this._viewContainerRef,
          {
            type: "warning",
            title: "Page in use",
            hideCloseButton: true,
            hideIcon: true,
            backDropOpacity: 0.9,
            content: TakeOverConfirmDialogComponent,
            contentContext: {
              currentUsername: enterResult.currentUsername ?? "Another user",
            },
            timeout: null as any,
          },
        );
        if (!confirmed) {
          this._router.navigateByUrl("/scheduler/calendar");
          return false;
        }
        await this._socketService.takeOverBroadcastEditPage(
          broadcastId,
          currentUser.username,
          this._broadcastPageClientId,
        );
        this._socketService.registerBroadcastEditPageListener(
          this._onOtherUserEnteredBroadcastEditBound,
        );
      }
      this._isBroadcastEditPage = true;
      this._isBroadcastLivePage = false;
      return true;
    }

    if (currentPath.startsWith("/broadcasts/live/")) {
      this._broadcastPageClientId =
        this._commonUtils.generateGuid() + "_broadcastPage";
      const currentUser = this._userService.getCurrentUser();
      this._onOtherUserEnteredBroadcastLiveBound =
        this._onOtherUserEnteredBroadcastLive.bind(this);
      const enterResult = await this._socketService.enteredBroadcastLivePageEventHandler(
        broadcastId,
        currentUser.username,
        this._broadcastPageClientId,
        this._onOtherUserEnteredBroadcastLiveBound,
      );
      if (enterResult.alreadyInUse) {
        const confirmed = await this._dialogService.openDialog(
          this._viewContainerRef,
          {
            type: "warning",
            title: "Page in use",
            hideCloseButton: true,
            hideIcon: true,
            backDropOpacity: 0.9,
            content: TakeOverConfirmDialogComponent,
            contentContext: {
              currentUsername: enterResult.currentUsername ?? "Another user",
            },
            timeout: null as any,
          },
        );
        if (!confirmed) {
          this._router.navigateByUrl("/scheduler/calendar");
          return false;
        }
        await this._socketService.takeOverBroadcastLivePage(
          broadcastId,
          currentUser.username,
          this._broadcastPageClientId,
        );
        this._socketService.registerBroadcastLivePageListener(
          this._onOtherUserEnteredBroadcastLiveBound,
        );
      }
      this._isBroadcastLivePage = true;
      this._isBroadcastEditPage = false;
    }

    return true;
  }

  ngOnInit(): void {
    console.log("BroadcastEditorComponent->ngOnInit", this.vm);
    this._broadcastPresetUpdatedSubscription =
      this._broadcastsPresetsRepository.updated.subscribe((broadcastPreset) => {
        if (this.vm && broadcastPreset.id === this.vm.id) {
          this.vm.updateSwitchers(broadcastPreset.switchers);
        }
      });
    this.mamCollapsedSubscription = this._ui.EVENTS.MamCollapsed.subscribe(
      (value) => {
        this.mamCollapsed = !!value;
        if (this._mamCollapsedTimeoutId != null) {
          clearTimeout(this._mamCollapsedTimeoutId);
        }
        this._mamCollapsedTimeoutId = setTimeout(() => {
          this._mamCollapsedTimeoutId = null;
          if (this.previewVideoElement?.nativeElement && this.vm) {
            this.vm.adjustAspectRatio(
              this.previewVideoElement.nativeElement,
              16 / 9,
            );
          }
          if (this.programVideoElement?.nativeElement && this.vm) {
            this.vm.adjustAspectRatio(
              this.programVideoElement.nativeElement,
              16 / 9,
            );
          }
        }, 1);
      },
    );
  }

  ngOnDestroy(): void {
    this._destroyed = true;
    if (this._otherUserEnteredCleanupTimeoutId != null) {
      clearTimeout(this._otherUserEnteredCleanupTimeoutId);
      this._otherUserEnteredCleanupTimeoutId = null;
    }
    if (this._mamCollapsedTimeoutId != null) {
      clearTimeout(this._mamCollapsedTimeoutId);
      this._mamCollapsedTimeoutId = null;
    }
    if (this._debugDragMouseMoveHandler) {
      document.removeEventListener("mousemove", this._debugDragMouseMoveHandler);
      this._debugDragMouseMoveHandler = null;
    }
    if (this._debugDragMouseUpHandler) {
      document.removeEventListener("mouseup", this._debugDragMouseUpHandler);
      this._debugDragMouseUpHandler = null;
    }
    this._broadcastEditorCleanup({ killChannels: false });
    this.vm?.onDestroy();
    this.vm = null;
    if (this._routeParamsSubscription) {
      this._routeParamsSubscription.unsubscribe();
      this._routeParamsSubscription = null;
    }
    if (this._routerEndSubscription) {
      this._routerEndSubscription.unsubscribe();
      this._routerEndSubscription = null;
    }
    if (this._broadcastPresetUpdatedSubscription) {
      this._broadcastPresetUpdatedSubscription.unsubscribe();
      this._broadcastPresetUpdatedSubscription = null;
    }
    if (this.mamCollapsedSubscription) {
      this.mamCollapsedSubscription.unsubscribe();
      this.mamCollapsedSubscription = null;
    }
  }
  /** Returns false when it redirected and the caller should stop initializing. */
  async checkIfToAllowLiveMode(): Promise<boolean> {
    const currentPath = this._router.url;
    if (currentPath.startsWith("/broadcasts/live/")) {
      const globalBroadcastMode =
        await this._channelBroadcastModeChecker.getGlobalBroadcastMode();
      if (globalBroadcastMode !== EChannelBroadcastMode.Off) {
        this.vm.editMode = false;
        this.vm.allowLiveMode = true;
      } else {
        this._router.navigate(["/broadcasts/edit/", this.vm.id], {
          queryParamsHandling: "preserve",
          state: {
            fromScheduler: true,
          },
        });
        return false;
      }
    } else {
      this.vm.editMode = true;
      this.vm.allowLiveMode = false;
    }
    return true;
  }

  private async _registerBroadcastPageSingleUser(): Promise<void> {
    const currentPath = this._router.url;
    const currentUser = this._userService.getCurrentUser();
    this._broadcastPageClientId =
      this._commonUtils.generateGuid() + "_broadcastPage";

    if (currentPath.startsWith("/broadcasts/live/")) {
      this._onOtherUserEnteredBroadcastLiveBound =
        this._onOtherUserEnteredBroadcastLive.bind(this);
      await this._socketService.enteredBroadcastLivePageEventHandler(
        this.vm.id,
        currentUser.username,
        this._broadcastPageClientId,
        this._onOtherUserEnteredBroadcastLiveBound,
      );
      this._isBroadcastEditPage = false;
      this._isBroadcastLivePage = true;
    } else {
      this._isBroadcastEditPage = false;
      this._isBroadcastLivePage = false;
    }
  }

  private async _isPresetLiveForChannel(
    channelId: number,
    showId: string,
  ): Promise<boolean> {
    return this._isPresetLiveForChannelByPresetId(
      channelId,
      showId,
      this.vm.id,
    );
  }

  private async _isPresetLiveForChannelByPresetId(
    channelId: number,
    showId: string,
    presetId: string,
  ): Promise<boolean> {
    const show = this._calendarService.getShowFromCache(showId);
    return !!(
      show &&
      "broadcastPresetUid" in show &&
      (show as { broadcastPresetUid: string }).broadcastPresetUid === presetId
    );
  }

  private _onOtherUserEnteredBroadcastEdit(
    broadcastPresetUid: string,
    username: string,
    time: Date,
  ): void {
    this._ngZone.run(() => {
      this._killBroadcastPreviewChannels();
      this._dialogService
        .openDialog(this._viewContainerRef, {
          type: "warning",
          title: "Broadcaster Live Page Disconnected",
          hideCloseButton: true,
          hideIcon: true,
          backDropOpacity: 0.9,
          confirmButtonText: "CLOSE",
          content: LiveOtherUserEnteredComponent,
          contentContext: {
            otherUsernameConnected: username,
            pageName: "the Broadcaster Live Page",
          },
          timeout: null as any,
        })
        .then(() => {
          this._router.navigateByUrl("/scheduler/calendar");
        });
      if (this._otherUserEnteredCleanupTimeoutId != null) {
        clearTimeout(this._otherUserEnteredCleanupTimeoutId);
      }
      this._otherUserEnteredCleanupTimeoutId = setTimeout(() => {
        this._otherUserEnteredCleanupTimeoutId = null;
        this._broadcastEditorCleanup({ killChannels: false });
      }, 2000);
    });
  }

  private _onOtherUserEnteredBroadcastLive(
    broadcastPresetUid: string,
    username: string,
    time: Date,
  ): void {
    this._ngZone.run(() => {
      this._killBroadcastPreviewChannels();
      this._dialogService
        .openDialog(this._viewContainerRef, {
          type: "warning",
          title: "Live Page Disconnected",
          hideCloseButton: true,
          hideIcon: true,
          backDropOpacity: 0.9,
          confirmButtonText: "CLOSE",
          content: LiveOtherUserEnteredComponent,
          contentContext: {
            otherUsernameConnected: username,
            pageName: "the Broadcaster Live Page",
          },
          timeout: null as any,
        })
        .then(() => {
          this._router.navigateByUrl("/scheduler/calendar");
        });
      if (this._otherUserEnteredCleanupTimeoutId != null) {
        clearTimeout(this._otherUserEnteredCleanupTimeoutId);
      }
      this._otherUserEnteredCleanupTimeoutId = setTimeout(() => {
        this._otherUserEnteredCleanupTimeoutId = null;
        this._broadcastEditorCleanup({ killChannels: false });
      }, 2000);
    });
  }

  /**
   * Cleans up socket listeners and optionally deletes preview/program channels.
   * Pass killChannels: false to keep channels and their cache so they can be reused (by the
   * same user returning or by the user who took over), avoiding expensive DeletePreviewChannel
   * and CreateChannelCacheWithFullPlaylist. The hub reuses existing channels per broadcast.
   */
  private _broadcastEditorCleanup(options?: { killChannels: boolean }): void {
    if (!this.vm) {
      return;
    }
    if (
      this._isBroadcastEditPage &&
      this._onOtherUserEnteredBroadcastEditBound
    ) {
      this._socketService.stopListeningForBroadcastEditPage(
        this._onOtherUserEnteredBroadcastEditBound,
      );
      this._socketService.leaveBroadcastEditPage(this.vm.id).catch(() => {});
      this._isBroadcastEditPage = false;
      this._onOtherUserEnteredBroadcastEditBound = null;
    }
    if (
      this._isBroadcastLivePage &&
      this._onOtherUserEnteredBroadcastLiveBound
    ) {
      this._socketService.stopListeningForBroadcastLivePage(
        this._onOtherUserEnteredBroadcastLiveBound,
      );
      this._socketService.leaveBroadcastLivePage(this.vm.id).catch(() => {});
      this._isBroadcastLivePage = false;
      this._onOtherUserEnteredBroadcastLiveBound = null;
    }
    if (options?.killChannels === true) {
      this._killBroadcastPreviewChannels();
    }
  }

  /**
   * Kills the preview channel and the program channel (including operational channel program)
   * when the user is kicked or leaves the broadcast editor.
   */
  private _killBroadcastPreviewChannels(): void {
    if (!this.vm) {
      return;
    }
    const preview = this.vm.previewWindowData;
    const program = this.vm.programWindowData;
    if (preview) {
      this.vm.deactivateWebRTCChannel(preview).catch(() => {});
    }
    if (program) {
      this.vm.deactivateWebRTCChannel(program).catch(() => {});
    }
  }

  onDebugWindowDragHandleMouseDown(event: MouseEvent): void {
    // Prevent event propagation to avoid triggering other click handlers
    event.stopPropagation();

    const debugWindow = (event.target as HTMLElement).closest(
      ".debug-window",
    ) as HTMLElement;

    // Make sure the debug window has position absolute
    if (window.getComputedStyle(debugWindow).position !== "absolute") {
      const rect = debugWindow.getBoundingClientRect();
      debugWindow.style.position = "absolute";
      debugWindow.style.left = rect.left + "px";
      debugWindow.style.top = rect.top + "px";
      debugWindow.style.width = rect.width + "px";
      debugWindow.style.zIndex = "1000";
    }

    // Initial position
    const startX = event.clientX;
    const startY = event.clientY;
    const startLeft = parseInt(debugWindow.style.left) || 0;
    const startTop = parseInt(debugWindow.style.top) || 0;

    // Mouse move handler (stored so ngOnDestroy can remove if user navigates away while dragging)
    this._debugDragMouseMoveHandler = (moveEvent: MouseEvent) => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;

      debugWindow.style.left = `${startLeft + dx}px`;
      debugWindow.style.top = `${startTop + dy}px`;

      moveEvent.preventDefault();
    };

    this._debugDragMouseUpHandler = () => {
      if (this._debugDragMouseMoveHandler) {
        document.removeEventListener("mousemove", this._debugDragMouseMoveHandler);
        this._debugDragMouseMoveHandler = null;
      }
      if (this._debugDragMouseUpHandler) {
        document.removeEventListener("mouseup", this._debugDragMouseUpHandler);
        this._debugDragMouseUpHandler = null;
      }
    };

    document.addEventListener("mousemove", this._debugDragMouseMoveHandler);
    document.addEventListener("mouseup", this._debugDragMouseUpHandler);
  }
  onSwitcherItemDrop(event: DragEvent, switcherItem: SwitcherItemViewModel) {
    event.preventDefault();

    // Refuse drops onto covered tiles (they are [hidden] but still in the DOM)
    if (this.vm.selectedSwitcher.isCovered(switcherItem.index)) {
      this._ui.notify(this._ui.EVENTS.ShowAlert, {
        title: 'Move Item Failed',
        message: 'You can only drop items onto empty switcher slots!',
      });
      return;
    }

    // Check if this is an internal drag (switcher item to switcher item)
    if (event.dataTransfer?.types.includes("text/switcher-item")) {
      const draggedItemData = JSON.parse(
        event.dataTransfer.getData("text/switcher-item"),
      );

      // Find the dragged item in the switcher
      const draggedItem = this.vm.selectedSwitcher.itemsVMs.find(
        (item) => item.uid === draggedItemData.uid,
      );

      if (!draggedItem) {
        return;
      }

      // Don't allow dropping on the same slot
      if (draggedItem.index === switcherItem.index) {
        return;
      }

      // Target must be empty (not a non-dragged occupied slot)
      if (switcherItem.type !== ESwitcherAssetType.Empty) {
        this._ui.notify(this._ui.EVENTS.ShowAlert, {
          title: "Move Item Failed",
          message: "You can only move items to empty switcher slots!",
        });
        return;
      }

      const items = this.vm.selectedSwitcher.itemsVMs;
      const sourceIndex = draggedItem.index;
      const targetIndex = switcherItem.index;

      // For spanning masters, validate the entire required area at the target
      if (draggedItem.colSpan > 1 || draggedItem.rowSpan > 1) {
        const GRID_COLS = 6;
        const GRID_ROWS = 2;
        const targetRow = Math.floor(targetIndex / GRID_COLS);
        const targetCol = targetIndex % GRID_COLS;
        const sourceRow = Math.floor(sourceIndex / GRID_COLS);
        const sourceCol = sourceIndex % GRID_COLS;

        // Check grid boundary
        if (
          targetCol + draggedItem.colSpan > GRID_COLS ||
          targetRow + draggedItem.rowSpan > GRID_ROWS
        ) {
          this._ui.notify(this._ui.EVENTS.ShowAlert, {
            title: "Move Item Failed",
            message: "Not enough space at destination (out of grid bounds).",
          });
          return;
        }

        // Collect the source's own covered tile indices so they are not counted
        // as obstacles
        const sourceTiles = new Set<number>();
        for (let r = 0; r < draggedItem.rowSpan; r++) {
          for (let c = 0; c < draggedItem.colSpan; c++) {
            sourceTiles.add((sourceRow + r) * GRID_COLS + (sourceCol + c));
          }
        }

        // Verify every destination tile is either empty or a source tile
        for (let r = 0; r < draggedItem.rowSpan; r++) {
          for (let c = 0; c < draggedItem.colSpan; c++) {
            const idx = (targetRow + r) * GRID_COLS + (targetCol + c);
            if (sourceTiles.has(idx)) {
              continue;
            }
            const tile = items[idx];
            if (!tile || tile.type !== ESwitcherAssetType.Empty) {
              this._ui.notify(this._ui.EVENTS.ShowAlert, {
                title: "Move Item Failed",
                message:
                  "Not enough empty space at destination for a multi-tile item.",
              });
              return;
            }
            if (this.vm.selectedSwitcher.isCovered(idx)) {
              this._ui.notify(this._ui.EVENTS.ShowAlert, {
                title: "Move Item Failed",
                message:
                  "Not enough empty space at destination for a multi-tile item.",
              });
              return;
            }
          }
        }

        // Clear the source's covered tiles (the master slot will be overwritten
        // in the swap below; non-anchor source tiles just need to remain Empty)
        for (const idx of sourceTiles) {
          if (idx !== sourceIndex) {
            items[idx].colSpan = 1;
            items[idx].rowSpan = 1;
          }
        }
      }

      // Swap the items in the array
      [items[sourceIndex], items[targetIndex]] = [
        items[targetIndex],
        items[sourceIndex],
      ];

      // Update their index properties to match their new positions
      items[sourceIndex].index = sourceIndex;
      items[targetIndex].index = targetIndex;

      // Repaint the UI
      this.vm.forceUIUpdate();
      this._changeDetectorRef.detectChanges();
      this.vm.updateBroadcastPreset();
      return;
    }

    // Handle external drag (from media library, etc.)
    if (
      event?.dataTransfer?.types?.length == 2 &&
      event?.dataTransfer?.types.includes("text/plain") &&
      event?.dataTransfer?.types.includes("text/html")
    ) {
      // prevent default drag and drop behavior to avoid the default behavior of the browser
      return;
    }
    if (switcherItem.type !== ESwitcherAssetType.Empty) {
      this._ui.notify(this._ui.EVENTS.ShowAlert, {
        title: "Add Item To switcher Failed",
        message: "You can only add items to empty switcher slots!",
      });
      return;
    }
    this.vm.onDrop(event, switcherItem);
  }

  @HostListener("document:keydown", ["$event"])
  onKeyDown(event: KeyboardEvent): void {
    if (
      !this.vm ||
      !this.vm.selectedSwitcher ||
      !this.vm.selectedSwitcher.itemsVMs
    ) {
      return;
    }

    // Don't trigger shortcuts if user is typing in an input field or contenteditable element
    const target = event.target as HTMLElement;
    if (
      target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      target.isContentEditable
    ) {
      return;
    }

    // Track Shift key states
    if (event.code === "ShiftLeft") {
      this._leftShiftPressed = true;
      return;
    }
    if (event.code === "ShiftRight") {
      this._rightShiftPressed = true;
      return;
    }

    // Map keys to indices: Q=0, W=1, E=2, R=3, T=4, Y=5, A=6, S=7, D=8, F=9, G=10, H=11
    const keyToIndexMap: { [key: string]: number } = {
      KeyQ: 0,
      KeyW: 1,
      KeyE: 2,
      KeyR: 3,
      KeyT: 4,
      KeyY: 5,
      KeyA: 6,
      KeyS: 7,
      KeyD: 8,
      KeyF: 9,
      KeyG: 10,
      KeyH: 11,
    };

    const keyIndex = keyToIndexMap[event.code];
    if (keyIndex === undefined) {
      return;
    }

    // Check if any Shift key is pressed
    if (!this._leftShiftPressed && !this._rightShiftPressed) {
      return;
    }

    // Get the switcher item at the mapped index
    const switcherItem = this.vm.selectedSwitcher.itemsVMs[keyIndex];
    if (!switcherItem || switcherItem.type == ESwitcherAssetType.Empty) {
      return;
    }

    // Prevent default behavior
    event.preventDefault();
    event.stopPropagation();

    // LEFT_SHIFT + LETTER -> Preview
    if (this._leftShiftPressed) {
      this.vm.onPreviewClicked(event, switcherItem);
    }
    // RIGHT_SHIFT + LETTER -> Program
    else if (this._rightShiftPressed) {
      this.vm.onProgramClicked(event, switcherItem);
    }
  }

  @HostListener("document:keyup", ["$event"])
  onKeyUp(event: KeyboardEvent): void {
    // Reset Shift key states when released
    if (event.code === "ShiftLeft") {
      this._leftShiftPressed = false;
    }
    if (event.code === "ShiftRight") {
      this._rightShiftPressed = false;
    }
  }
}
